import { notificationsApi } from '../lib/api'

// Browser-side half of web push. The service worker handlers live in
// public/push-sw.js; this module owns permission, subscription lifecycle, and
// keeping the server's copy of the subscription in sync.

export type PushStatus =
  | 'unsupported'      // browser has no Push API at all
  | 'needs-install'    // iOS: Push API only exists once the PWA is installed to the Home Screen
  | 'unconfigured'     // server has no VAPID keys, so nothing can be delivered
  | 'denied'           // user blocked notifications (only they can undo this, in browser settings)
  | 'default'          // never asked
  | 'granted'          // on

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/**
 * iOS/iPadOS expose the Push API only to a PWA that's been added to the Home Screen --
 * in plain Safari the whole API is absent. Worth distinguishing from "unsupported" so
 * we can tell the user the one thing that would actually fix it (install the app)
 * rather than a dead end.
 */
export function isIosNeedingInstall(): boolean {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  if (!isIos) return false
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  return !isStandalone && !('PushManager' in window)
}

// VAPID keys travel as base64url strings but PushManager.subscribe wants raw bytes.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}

// The public key is fetched once per session and reused -- it never changes for a
// given environment (rotating it would invalidate every stored subscription).
let cachedPublicKey: string | null | undefined

async function getPublicKey(): Promise<string | null> {
  if (cachedPublicKey !== undefined) return cachedPublicKey
  let key: string | null
  try {
    const res = await notificationsApi.getPushPublicKey()
    key = res.data?.data?.publicKey ?? null
  } catch {
    key = null
  }
  cachedPublicKey = key
  return key
}

export async function getPushStatus(): Promise<PushStatus> {
  if (isIosNeedingInstall()) return 'needs-install'
  if (!isPushSupported()) return 'unsupported'
  if ((await getPublicKey()) === null) return 'unconfigured'

  const permission = Notification.permission
  if (permission !== 'granted') return permission as PushStatus

  // Permission alone isn't proof anything is actually subscribed: the browser can
  // hold a granted permission with no live PushSubscription (subscribe() failed, the
  // push service dropped it, site data was cleared). Reporting "Notifications are on"
  // off the permission flag alone would tell the user they're covered when no push
  // can reach them. Fall back to 'default' so the UI offers a button that fixes it.
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    return subscription ? 'granted' : 'default'
  } catch {
    return 'default'
  }
}

/**
 * Ask for permission (if not already granted), subscribe this browser, and register
 * the subscription server-side. Returns the resulting status so the caller can render
 * the right message -- notably 'denied', which is unrecoverable in-app.
 */
export async function enablePush(): Promise<PushStatus> {
  const status = await getPushStatus()
  if (status !== 'default' && status !== 'granted') return status

  // Must be called from a user gesture on most browsers, hence this living behind an
  // explicit button rather than firing on page load.
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission as PushStatus

  await subscribeAndRegister()
  return 'granted'
}

async function subscribeAndRegister(): Promise<PushSubscription | null> {
  const publicKey = await getPublicKey()
  if (!publicKey) return null

  const registration = await navigator.serviceWorker.ready

  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    // Reuse it. Do NOT try to verify the VAPID key by comparing
    // subscription.options.applicationServerKey byte-by-byte -- an earlier version of
    // this function did, and the comparison returned false on Chrome even for a
    // correct subscription, so every page load unsubscribed and re-subscribed. Each
    // new subscription invalidates the previous one, which meant a user's registered
    // endpoint was stale most of the time: confirmed live on 2026-08-31, when a
    // rental-approved push found all of the renter's endpoints expired (410) and
    // silently reached nobody.
    //
    // Re-registering the same endpoint server-side is a cheap upsert and is what
    // repairs a rotated subscription, so that still happens on every call.
    await notificationsApi.subscribeToPush(existing.toJSON())
    return existing
  }

  return createSubscription(registration, publicKey)
}

async function createSubscription(
  registration: ServiceWorkerRegistration,
  publicKey: string
): Promise<PushSubscription> {
  try {
    const subscription = await registration.pushManager.subscribe({
      // Required to be true by every browser that implements the API: pushes must be
      // user-visible, not used for silent background sync.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    })
    await notificationsApi.subscribeToPush(subscription.toJSON())
    return subscription
  } catch (error) {
    // The browser itself is the authority on whether an existing subscription was
    // created with a different VAPID key: it throws InvalidStateError rather than
    // silently returning a mismatched one. That is the only reliable signal that a
    // re-subscribe is genuinely needed, so it -- not a hand-rolled key comparison --
    // is what drives the unsubscribe path.
    if ((error as DOMException)?.name !== 'InvalidStateError') throw error

    const stale = await registration.pushManager.getSubscription()
    await stale?.unsubscribe().catch(() => {})

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    })
    await notificationsApi.subscribeToPush(subscription.toJSON())
    return subscription
  }
}

/** Turn push off for this browser: drop it locally and forget it server-side. */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  const { endpoint } = subscription
  await subscription.unsubscribe().catch(() => {})
  await notificationsApi.unsubscribeFromPush(endpoint).catch(() => {
    // Local unsubscribe already stopped delivery. A stranded server row will be
    // pruned by PushNotificationService the first time the push service 410s it.
  })
}

/**
 * Re-register the existing subscription on app load. Cheap (an upsert) and repairs
 * the two cases that otherwise break delivery silently and permanently:
 *  - the push service rotated the subscription (pushsubscriptionchange, which the
 *    service worker can't report itself -- it has no access to the auth token)
 *  - the user granted permission on a device the server has no row for, e.g. after
 *    signing in as a different account on a shared browser
 */
export async function syncPushSubscription(): Promise<void> {
  try {
    if (!isPushSupported()) return
    if (Notification.permission !== 'granted') return
    await subscribeAndRegister()
  } catch {
    // Best-effort: never let a sync failure surface in the UI.
  }
}
